import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LDClient, LDFlagSet, initialize, LDContext } from 'launchdarkly-js-client-sdk';
import { useUserContext } from './UserContext';
import { isLocalStorageAvailable } from '../utils/storage';

interface FeatureFlagContextType {
  flags: LDFlagSet;
  isReady: boolean;
  client: LDClient | null;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

export function useFeatureFlag<T = boolean>(flagKey: string, defaultValue: T): T {
  const { flags, isReady } = useFeatureFlags();

  if (!isReady) {
    return defaultValue;
  }

  const flagValue = flags[flagKey];
  return flagValue !== undefined ? (flagValue as T) : defaultValue;
}

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [client, setClient] = useState<LDClient | null>(null);
  const [flags, setFlags] = useState<LDFlagSet>({});
  const [isReady, setIsReady] = useState(false);
  const { user, isLoggedIn } = useUserContext();

  useEffect(() => {
    const clientSideId = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID;

    if (!clientSideId) {
      console.warn('LaunchDarkly is not configured. Feature flags will not be available.');
      setIsReady(true);
      return;
    }

    const ldContext: LDContext = {
      kind: 'user',
      key: 'anonymous',
      anonymous: true,
    };

    // Only use localStorage bootstrap if localStorage is available
    const ldClient = initialize(clientSideId, ldContext, {
      streaming: true,
      sendEvents: true,
      bootstrap: isLocalStorageAvailable() ? 'localStorage' : undefined,
    });

    ldClient.on('ready', () => {
      console.log('LaunchDarkly client initialized');
      setFlags(ldClient.allFlags());
      setIsReady(true);
    });

    ldClient.on('change', () => {
      setFlags(ldClient.allFlags());
    });

    ldClient.on('error', (error) => {
      console.error('LaunchDarkly error:', error);
      setIsReady(true);
    });

    setClient(ldClient);

    return () => {
      ldClient.close();
    };
  }, []);

  useEffect(() => {
    if (!client || !isReady) {
      return;
    }

    const ldContext: LDContext = isLoggedIn && user
      ? {
          kind: 'user',
          key: user.uuid,
          email: user.email,
          name: user.name || undefined,
        }
      : {
          kind: 'user',
          key: 'anonymous',
          anonymous: true,
        };

    client.identify(ldContext).then(() => {
      setFlags(client.allFlags());
    }).catch((error) => {
      console.error('Error identifying user to LaunchDarkly:', error);
    });
  }, [user, isLoggedIn, client, isReady]);

  const value: FeatureFlagContextType = {
    flags,
    isReady,
    client,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
